// Påminnelser om delegeringsbrevens giltighetstid (Dok 52).
import { supabase } from "@/integrations/supabase/client";

export const REMINDER_STEPS = [60, 30, 7];

export interface DelegationReminder {
  id: string;
  toName: string;
  validTo: string;
  days: number;
  level: "expired" | "critical" | "warning" | "notice";
  acknowledged: boolean;
}

const daysUntil = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

export const reminderLevel = (days: number): DelegationReminder["level"] => {
  if (days < 0) return "expired";
  if (days <= 7) return "critical";
  if (days <= 30) return "warning";
  return "notice";
};

/** Delegeringsbrev vars giltighetstid närmar sig eller har passerats. */
export const fetchDelegationReminders = async (
  projectId: string
): Promise<DelegationReminder[]> => {
  const { data } = await supabase
    .from("delegation_letters")
    .select("id, to_name, valid_to, status, reminder_ack_at")
    .eq("project_id", projectId)
    .not("valid_to", "is", null);

  const rows =
    (data as
      | {
          id: string;
          to_name: string | null;
          valid_to: string | null;
          status: string;
          reminder_ack_at: string | null;
        }[]
      | null) ?? [];

  return rows
    .filter((r) => r.valid_to && !["aterlamnat", "upphort"].includes(r.status))
    .map((r) => {
      const days = daysUntil(r.valid_to as string);
      return {
        id: r.id,
        toName: r.to_name || "mottagare",
        validTo: r.valid_to as string,
        days,
        level: reminderLevel(days),
        acknowledged: Boolean(r.reminder_ack_at),
      };
    })
    .filter((r) => r.days <= REMINDER_STEPS[0])
    .sort((a, b) => a.days - b.days);
};

/** Kvitterar påminnelsen så att den inte visas som obehandlad. */
export const acknowledgeDelegationReminder = async (id: string, note?: string) => {
  const { data: auth } = await supabase.auth.getUser();
  return supabase
    .from("delegation_letters")
    .update({
      reminder_ack_at: new Date().toISOString(),
      reminder_ack_by: auth?.user?.id ?? null,
      reminder_ack_note: note ?? null,
    })
    .eq("id", id);
};
